import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Home,
  Users,
  Building2,
  UserCog,
  Stethoscope,
  PlusSquare,
  FileText,
  BarChart3,
  Package,
  ClipboardList,
  Settings,
  Search,
  AlertCircle,
  ChevronDown,
  MessageCircle,
  MessageCircleWarning,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useSidebar } from "@/modules/dashboard-shell/SidebarProvider";
import { cn } from "@/shared/lib/cn";
import { useNavActive } from "../useNavActive";

const iconMap = {
  home: Home,
  users: Users,
  building: Building2,
  "user-cog": UserCog,
  stethoscope: Stethoscope,
  "plus-square": PlusSquare,
  "file-text": FileText,
  "bar-chart": BarChart3,
  package: Package,
  clipboard: ClipboardList,
  settings: Settings,
  search: Search,
  "alert-circle": AlertCircle,
  "file-plus": FileText,
  "message-circle": MessageCircle,
  "message-alert": MessageCircleWarning,
} satisfies Record<IconName, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>>;

const navItemClass = (active: boolean) =>
  cn(
    "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
    active
      ? "bg-accent font-semibold text-accent-foreground shadow-sm"
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
  );

type NavLinkProps = {
  item: NavItem;
};

function NavChildLink({ child }: { child: NavChild }) {
  const { t } = useTranslation();
  const { close } = useSidebar();
  const { isEntryActive } = useNavActive();
  const isActive = isEntryActive(child.id, child.href);
  const label = t.safecheck.nav[child.id as SafecheckNavKey];

  return (
    <Link
      to={child.href}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      className={cn(
        "flex items-center rounded-xl py-2.5 pl-11 pr-3 text-sm transition-colors",
        isActive
          ? "bg-accent font-semibold text-accent-foreground"
          : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
      )}
    >
      {label}
    </Link>
  );
}

export function NavLink({ item }: NavLinkProps) {
  const { t } = useTranslation();
  const { close } = useSidebar();
  const { isItemActive, isParentActive } = useNavActive();
  const { id, iconName, href, children } = item;
  const Icon = iconMap[iconName];
  const label = t.safecheck.nav[id as SafecheckNavKey];

  const hasChildren = !!children?.length;
  const isActive = isItemActive(item);
  const parentOpen = hasChildren && isParentActive(item);
  const [expanded, setExpanded] = useState(parentOpen);

  useEffect(() => {
    if (parentOpen) setExpanded(true);
  }, [parentOpen]);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={cn("w-full", navItemClass(parentOpen))}
        >
          <Icon size={20} strokeWidth={1.75} />
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown
            size={16}
            className={cn("transition-transform", expanded && "rotate-180")}
          />
        </button>
        {expanded && (
          <div className="mt-1 space-y-0.5">
            {children.map((child) => (
              <NavChildLink key={child.id} child={child} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={href!}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      className={navItemClass(isActive)}
    >
      <Icon size={20} strokeWidth={1.75} />
      {label}
    </Link>
  );
}
